import { useRef, useState, useEffect } from 'react';
import { Tool, Point } from '../types/mspaints.tsx';
import { hexToRgb } from '../utils/colorUtils';

export function useCanvas() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        setCtx(context);
    }, []);

    const getMousePosition = (e: React.MouseEvent): Point | null => {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return null;

        return {
            x: Math.floor(e.clientX - rect.left),
            y: Math.floor(e.clientY - rect.top)
        };
    };

    const clearCanvas = () => {
        if (!ctx || !canvasRef.current) return;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    };

    const floodFill = async (
        startPoint: Point,
        color: string,
        setPixelInfo: (info: string) => void,
        speed: number
    ) => {
        if (!ctx || !canvasRef.current) return;

        const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
        const pixels = imageData.data;

        const startPos = (startPoint.y * canvasRef.current.width + startPoint.x) * 4;
        const startR = pixels[startPos];
        const startG = pixels[startPos + 1];
        const startB = pixels[startPos + 2];

        const newColorRGB = hexToRgb(color);
        if (!newColorRGB) return;

        const queue: Point[] = [startPoint];
        const visited = new Set<string>();
        let pixelsProcessed = 0;
        let batchSize = Math.floor(speed * 100); // Adjust batch size based on speed

        while (queue.length > 0) {
            let processedInBatch = 0;

            while (queue.length > 0 && processedInBatch < batchSize) {
                const point = queue.shift()!;
                const key = `${point.x},${point.y}`;

                if (visited.has(key)) continue;
                visited.add(key);

                const pos = (point.y * canvasRef.current.width + point.x) * 4;
                if (
                    pixels[pos] === startR &&
                    pixels[pos + 1] === startG &&
                    pixels[pos + 2] === startB
                ) {
                    pixels[pos] = newColorRGB.r;
                    pixels[pos + 1] = newColorRGB.g;
                    pixels[pos + 2] = newColorRGB.b;
                    pixelsProcessed++;
                    processedInBatch++;

                    // Highlight current processing area
                    ctx.putImageData(imageData, 0, 0);
                    ctx.fillStyle = 'rgba(255, 255, 0, 0.2)';
                    ctx.fillRect(point.x - 2, point.y - 2, 4, 4);

                    const neighbors: Point[] = [
                        { x: point.x - 1, y: point.y },
                        { x: point.x + 1, y: point.y },
                        { x: point.x, y: point.y - 1 },
                        { x: point.x, y: point.y + 1 }
                    ];

                    for (const neighbor of neighbors) {
                        if (
                            neighbor.x >= 0 &&
                            neighbor.x < canvasRef.current.width &&
                            neighbor.y >= 0 &&
                            neighbor.y < canvasRef.current.height
                        ) {
                            queue.push(neighbor);
                        }
                    }
                }
            }

            setPixelInfo(`Pixels filled: ${pixelsProcessed}\nQueue size: ${queue.length}\nVisited: ${visited.size}`);

            // Add delay between batches for visualization
            await new Promise(resolve => setTimeout(resolve, Math.max(1, 50 - speed * 45)));
        }

        ctx.putImageData(imageData, 0, 0);
        setTimeout(() => setPixelInfo(''), 2000);
    };

    return {
        canvasRef,
        ctx,
        isDrawing,
        setIsDrawing,
        getMousePosition,
        floodFill,
        clearCanvas
    };
}